import { agentsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  GoogleCatalogGroup,
  GoogleContentAttributeGroupApi,
} from '../types/google-catalog.types';

/**
 * Fetch the curated content-attribute taxonomy from the google agent.
 *
 * Defaults to `selectable_only=true` — the 13 entries the picker offers. Pass `false`
 * for the full curated set including fields that exist but are not offered.
 *
 * Groups keep server order (CONTENT → DESCRIPTIVE → STRUCTURAL → SEARCH) and attributes
 * are sorted by `sort_order` within a group, so the panel never depends on JSON key
 * order. The wire shape is normalised to camelCase here so components never see it.
 */
export async function fetchGoogleContentAttributes(
  selectableOnly = true,
): Promise<GoogleCatalogGroup[]> {
  const groups = await agentsClient
    .get(endpoints.agents.googleContentAttributes(), {
      searchParams: { selectable_only: selectableOnly },
    })
    .json<GoogleContentAttributeGroupApi[]>();

  return groups.map((group) => ({
    id: group.group,
    name: group.group_name,
    label: group.group_label,
    attributes: [...group.attributes]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((attr) => ({
        key: attr.key,
        label: attr.display_name,
        selectable: attr.selectable,
        generation: attr.generation,
        controlChannel: attr.control_channel,
        requires: attr.requires,
      })),
  }));
}
